import React, { useEffect } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useWatch } from "react-hook-form";

import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { Tabs } from "src/design-system/Tabs";
import { usePromoteFlowVersionToManifest } from "src/flowContainer/hooks/useManifestBuilder";
import { PromoteFlowVersionForm } from "src/flowContainer/ManifestPromotion/PromoteFlowVersionForm";
import { promoteFlowVersionSchema } from "src/flowContainer/ManifestPromotion/schema";
import {
  IntegrationType,
  PromoteFlowVersionFormData,
} from "src/flowContainer/ManifestPromotion/types";
import {
  CustomConnectionNodeData,
  extractCustomConnectionNodeData,
} from "src/flowContainer/ManifestPromotion/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useAuthStore } from "src/store/AuthStore";

type PromoteFlowVersionModalProps = {
  open: boolean;
  onClose: () => void;
  flowVersionId: string;
  afterLeave?: () => void;
};

const getStorageKey = (flowId: string, flowVersionId: string): string =>
  `promoteFlowVersion:${flowId}:${flowVersionId}`;

const DEFAULT_FORM_VALUES: PromoteFlowVersionFormData = {
  integrationType: "general",
  providerType: "new",
  onePasswordLinks: [],
  documentations: [],
  errorHandling: "",
  comments: "",
  organizationId: "",
  workspaceId: "",
  organizationName: "",
  workspaceName: "",
};

const INTEGRATION_TYPE_TABS: { key: IntegrationType; label: string }[] = [
  { key: "general", label: "General" },
  { key: "customer", label: "Customer specific" },
];

const getPersistedFormData = (
  storageKey: string,
): PromoteFlowVersionFormData => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return { ...DEFAULT_FORM_VALUES, ...JSON.parse(stored) };
    }
  } catch {}
  return DEFAULT_FORM_VALUES;
};

export const PromoteFlowVersionModal: React.FC<
  PromoteFlowVersionModalProps
> = ({ open, onClose, flowVersionId, afterLeave }) => {
  const { workspace, flow, version } = useAuthoringContext();
  const userEmail = useAuthStore((s) => s.signed_in_email);
  const { mutateAsync: promoteFlowVersion, isPending } =
    usePromoteFlowVersionToManifest();

  const customNodeData: CustomConnectionNodeData | null =
    extractCustomConnectionNodeData(version.graph);

  const storageKey = getStorageKey(flow.id, flowVersionId);

  const form = useForm<PromoteFlowVersionFormData>({
    resolver: yupResolver(promoteFlowVersionSchema),
    defaultValues: getPersistedFormData(storageKey),
  });

  const integrationType = useWatch({
    control: form.control,
    name: "integrationType",
  });

  useEffect(() => {
    const subscription = form.watch((values) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(values));
      } catch {}
    });
    return () => subscription.unsubscribe();
  }, [form, storageKey]);

  const handleSubmit = async (data: PromoteFlowVersionFormData) => {
    await promoteFlowVersion({
      flowVersionId,
      userEmail: userEmail ?? "",
      formData: data,
    });
    localStorage.removeItem(storageKey);
    form.reset(DEFAULT_FORM_VALUES);
    onClose();
  };

  return (
    <Modal afterLeave={afterLeave} open={open} size="sm" onClose={onClose}>
      <Modal.Header description="Provide context for the integration team for production review.">
        Promote Decision Flow to integration{" "}
      </Modal.Header>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Modal.Content>
          {/*
            Both panels render the same form, which shows the fields belonging to
            the selected integration type. Mounting it twice is safe because every
            value lives in the parent `useForm`, not in the panel. A later patch
            can split these into two presentational panels.
          */}
          <Tabs
            dataLoc="promote-integration-type-tabs"
            selectedKey={integrationType}
            tabs={INTEGRATION_TYPE_TABS.map(({ key, label }) => ({
              key,
              label,
              content: (
                <div className="pt-6">
                  <PromoteFlowVersionForm
                    baseUrl={workspace.base_url}
                    customNodeData={customNodeData}
                    form={form}
                  />
                </div>
              ),
            }))}
            onChange={(key) =>
              form.setValue("integrationType", key as IntegrationType)
            }
          />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="promote-flow-version-submit"
              htmlType="submit"
              isLoading={isPending}
              variant="primary"
            >
              Promote
            </Button>
          }
          secondaryButton={
            <Button
              isDisabled={isPending}
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
